import type { Movimiento, Origen } from "../api/tipos.ts";

/**
 * ¿Qué significa decidir sobre ESTE pago?
 *
 * En la bandeja la pregunta no existe: allí solo hay pagos que nadie ha
 * resuelto, y decidir es decidir. En la lista de movimientos están los tres
 * casos mezclados, y pulsar el mismo botón significa cosas distintas:
 *
 * - `NUEVA`      — nadie lo ha decidido. Igual que la bandeja.
 * - `A_MAQUINA`  — lo resolvió el motor. Decidir es corregirlo.
 * - `A_PERSONA`  — lo decidió alguien. Decidir es reemplazar su decisión.
 *
 * Hacer los tres iguales sería mentir por omisión: quien pulsa tiene derecho a
 * saber que está sobrescribiendo el trabajo de una compañera ANTES de pulsar,
 * no a enterarse después en el registro.
 *
 * Corregir es seguro por construcción y por eso se ofrece: las decisiones son
 * append-only —la última sobre una clave es la vigente— y las identidades
 * aprendidas se derivan de ellas, así que una corrección deshace sola lo que
 * enseñó la equivocada (SPEC-013 §3). Nada de esto exige tocar la base.
 */
export const CLASE = { NUEVA: "NUEVA", A_MAQUINA: "A_MAQUINA", A_PERSONA: "A_PERSONA" } as const;
export type Clase = (typeof CLASE)[keyof typeof CLASE];

/** Los orígenes que la bandeja considera pendientes (los tres de su tabla de prioridad). */
const ESPERAN_DECISION: Origen[] = ["pendiente", "humano-obsoleto", "clave-ambigua"];

export function claseDe(m: Pick<Movimiento, "origen">): Clase {
  if (ESPERAN_DECISION.includes(m.origen)) return CLASE.NUEVA;
  return m.origen === "humano" ? CLASE.A_PERSONA : CLASE.A_MAQUINA;
}

/**
 * El aviso que precede a los botones, o null cuando no hay nada que advertir.
 *
 * `quien` y `cuando` se pasan ya formateados por quien pinta: aquí no se
 * formatean fechas, para que esto se pueda probar sin depender de la zona
 * horaria de quien corre las pruebas — que es justo lo que escondió el desfase
 * de cinco horas en la hora del banco.
 */
export function avisoDeCorreccion(
  m: Pick<Movimiento, "origen" | "quien">,
  hace: string,
): { tono: "riesgo" | "aviso"; titulo: string; texto: string } | null {
  const clase = claseDe(m);
  if (clase === CLASE.NUEVA) return null;

  if (clase === CLASE.A_PERSONA) {
    return {
      tono: "riesgo",
      titulo: "Vas a reemplazar una decisión",
      texto: `${m.quien ?? "Alguien"} ya decidió este pago${hace ? ` ${hace}` : ""}. Lo que elijas aquí pasa a ser lo vigente, y queda registrado que fuiste tú.`,
    };
  }

  return {
    tono: "aviso",
    titulo: "Vas a corregir al sistema",
    texto:
      "Este pago lo resolvió 4drando por su cuenta. Tu decisión manda sobre la suya, y de paso le enseña a no repetir el error.",
  };
}
